import React from "react";
import { User } from "../types/user";
import { useRosterEditor } from "../hooks/useRosterEditor";
import "./SelectedUserEditor.scss";

interface SelectedUserEditorProps {
  users: User[];
  setUsers: React.Dispatch<React.SetStateAction<User[]>>;
  selectedUserIndex: number | null;
  whichUserPaidIndex: number;
  setWhichUserPaidIndex: React.Dispatch<React.SetStateAction<number>>;
  canEdit: boolean;
  // the mate a guest says they are, if any — lets them edit just their own
  // venmo (never anyone else's, and never their name)
  meId?: string | null;
  onGuestVenmoChange?: (userId: string, venmo: string) => void;
}

// Row under the mates bar on the shared check (/:id) showing the selected
// mate's name + venmo; no delete (mates can't be removed once shared). The
// owner can edit any mate, including "Me"; a guest can edit only their own
// venmo. Everything else shows read-only.
const SelectedUserEditor: React.FC<SelectedUserEditorProps> = ({
  users,
  setUsers,
  selectedUserIndex,
  whichUserPaidIndex,
  setWhichUserPaidIndex,
  canEdit,
  meId = null,
  onGuestVenmoChange,
}) => {
  const { patchUser, commitUser } = useRosterEditor({
    users,
    setUsers,
    whichUserPaidIndex,
    setWhichUserPaidIndex,
  });

  const selected =
    selectedUserIndex !== null ? (users[selectedUserIndex] ?? null) : null;
  const isGuestSelf = !canEdit && !!selected && selected.id === meId;
  // the owner edits any mate's name + venmo; a guest only their own venmo —
  // and never the first mate's ("Me", the owner/payer, whose venmo everyone
  // pays), even when they've picked it as themselves
  const canEditName = canEdit && !!selected;
  const canEditVenmo =
    !!selected && (canEdit || (isGuestSelf && selectedUserIndex !== 0));

  const handleNameChange = (name: string) => {
    if (selectedUserIndex !== null) patchUser(selectedUserIndex, { name });
  };

  // a guest's own venmo persists straight to the shared receipt (the generic
  // roster-content save is owner-only)
  const handleVenmoChange = (venmo: string) => {
    if (selectedUserIndex === null || !selected) return;
    if (canEdit) patchUser(selectedUserIndex, { venmo: venmo || undefined });
    else onGuestVenmoChange?.(selected.id, venmo);
  };

  const commitSelectedUser = () => {
    if (canEdit && selectedUserIndex !== null) commitUser(selectedUserIndex);
  };

  // a mate is always selected on the shared check (no deselecting); this only
  // hits for a moment while it loads
  if (!selected) return null;

  // read-only where you can't edit, so the row's height doesn't change as the
  // selection moves between mates you can and can't edit
  return (
    <div className="selected-user-editor">
      <input
        className={`edit-name${canEditName ? "" : " readonly"}`}
        placeholder="Name"
        value={selected.name}
        readOnly={!canEditName}
        tabIndex={canEditName ? undefined : -1}
        onChange={(e) => handleNameChange(e.target.value)}
        onBlur={commitSelectedUser}
      />
      <input
        className={`edit-venmo${canEditVenmo ? "" : " readonly"}`}
        placeholder="Venmo"
        value={selected.venmo ?? ""}
        readOnly={!canEditVenmo}
        tabIndex={canEditVenmo ? undefined : -1}
        onChange={(e) => handleVenmoChange(e.target.value)}
        onBlur={commitSelectedUser}
      />
    </div>
  );
};

export default SelectedUserEditor;
